/**
 * Article share controls.
 *
 * The network links are plain anchors in the markup and work without this file;
 * all this adds is the copy-link button, which cannot be expressed as a link.
 * No third-party SDK is loaded - a share button is not worth a tracker.
 *
 * The button is rendered `hidden` and revealed here, so a visitor whose browser
 * cannot copy is never shown a control that does nothing.
 */

import { Component } from '@theme/component';

class AsxShare extends Component {
  requiredRefs = ['copy'];

  connectedCallback() {
    super.connectedCallback();

    const button = this.refs.copy;
    if (!(button instanceof HTMLButtonElement)) return;
    if (!navigator.clipboard) return;

    button.hidden = false;
    this.#idleLabel = button.textContent?.trim() ?? '';
  }

  /** @type {string} */
  #idleLabel = '';

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #reset;

  /** Copies the article URL and confirms it in the button itself. */
  copy = async () => {
    const button = this.refs.copy;
    if (!(button instanceof HTMLButtonElement)) return;

    try {
      await navigator.clipboard.writeText(this.dataset.url || window.location.href);
    } catch {
      // A refused clipboard is not worth an error state: the URL is in the
      // address bar either way.
      return;
    }

    button.textContent = button.dataset.copiedLabel || this.#idleLabel;
    button.setAttribute('data-copied', '');

    clearTimeout(this.#reset);
    this.#reset = setTimeout(() => {
      button.textContent = this.#idleLabel;
      button.removeAttribute('data-copied');
    }, 2400);
  };
}

if (!customElements.get('asx-share')) {
  customElements.define('asx-share', AsxShare);
}

export default AsxShare;
